import React, {
  createContext,
  useContext,
  useMemo,
  useState,
  useCallback,
} from "react";
import { useMutation } from "@apollo/client";
import { enqueueSnackbar } from "../state/ducks/notifiers";
import { RESEND_FAX } from "../mutations/resendFax";
import { useDispatch } from "react-redux";
import { disableSendButton, enableSendButton } from "../state/ducks/fax";

const SentFaxesContext = createContext();
export const SentFaxesProvider = ({ children }) => {
  const dispatch = useDispatch();
  const [disableResendFaxButton, setDisableResendFaxButton] = useState(true);
  const [openResendFaxConfirmationDialog, setOpenResendFaxConfirmationDialog] =
    useState(false);
  const [selectedFax, setSelectedFax] = useState(null);

  const [resendFaxMutation] = useMutation(RESEND_FAX, {
    onCompleted: () => {
      dispatch(
        enqueueSnackbar({
          message: "Fax successfully sent!",
          options: { variant: "success" },
        })
      );
      setDisableResendFaxButton(true);
      setOpenResendFaxConfirmationDialog(false);
      dispatch(enableSendButton()); // Re-enable button on completion
    },
    onError: (error) => {
      dispatch(
        enqueueSnackbar({
          message: "Fax resend failed",
          options: { variant: "error" },
        })
      );
      dispatch(enableSendButton()); // Re-enable button on error
    },
  });

  const resendFax = useCallback(
    async (variables) => {
      dispatch(disableSendButton()); // Disable button before mutation
      await resendFaxMutation({ variables });
    },
    [dispatch, resendFaxMutation]
  );

  const sentFaxes = useMemo(
    () => ({
      disableResendFaxButton,
      setDisableResendFaxButton,
      openResendFaxConfirmationDialog,
      setOpenResendFaxConfirmationDialog,
      resendFax,
      selectedFax,
      setSelectedFax,
    }),
    [
      disableResendFaxButton,
      openResendFaxConfirmationDialog,
      resendFax,
      selectedFax,
      dispatch, // Added dispatch to dependencies
    ]
  );

  return (
    <SentFaxesContext.Provider value={sentFaxes}>
      {children}
    </SentFaxesContext.Provider>
  );
};

export const useSentFaxes = () => {
  const context = useContext(SentFaxesContext);
  if (context === undefined) {
    return new Error("SentFaxes context is undefined");
  }
  return context;
};
